import { useState, type FormEvent } from 'react';
import { ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button, Field, Input } from '../components/ui';
import type { TotpChallenge } from '../lib/types';

export function LoginPage() {
  const { login, loginTotp } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [challenge, setChallenge] = useState<TotpChallenge | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const failed = (err: unknown) =>
    setError(err instanceof ApiError ? err.message : 'Холбогдож чадсангүй. Сүлжээгээ шалгана уу.');

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      // TOTP тохируулсан бол хэрэглэгч ОРООГҮЙ хэвээр, challenge буцна
      const ch = await login(username.trim(), password);
      if (ch) {
        setChallenge(ch);
        setPassword('');
      }
    } catch (err) {
      failed(err);
    } finally {
      setBusy(false);
    }
  }

  async function submitTotp(e: FormEvent) {
    e.preventDefault();
    if (!challenge) return;
    setError(null);
    setBusy(true);
    try {
      await loginTotp(challenge.challengeId, code);
    } catch (err) {
      failed(err);
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  function back() {
    setChallenge(null);
    setCode('');
    setError(null);
  }

  return (
    <div className="relative grid min-h-dvh place-items-center overflow-hidden px-4 py-8">
      {/*
        Дэвсгэр — зураг + харанхуйлуулагч. `absolute` тул layout-д огт
        оролцохгүй: зураг ирээгүй ч карт хөдлөхгүй, доор нь `bg-hero`
        нөөц өнгө үлдэнэ (`hero-band.tsx`-тэй ижил зарчим).

        ⚠️ Хавтгай `bg-hero-scrim/78` нь контрастын БАТАЛГАА — хамгийн муу
        тохиолдол буюу цоо цагаан дэвсгэр дээр ч цагаан текстэд 6.9:1
        өгнө (AA нь 4.5). Тиймээс зураг СОЛИХОД дахин хэмжих
        шаардлагагүй; `images/README.md` дахь тууз нь зургийн бодит
        пикселээс хамаардаг тул тэрнээс ялгаатай. 0.72 нь доод хил.
      */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden bg-hero">
        <img
          src="/images/hero-reindeer.jpg"
          alt=""
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 size-full object-cover object-[50%_45%]"
        />
        <div className="absolute inset-0 bg-hero-scrim/78" />
        {/* Захыг гүнзгийрүүлнэ. ЗӨВХӨН нэмж харанхуйлдаг тул дээрх
            баталгааг сулруулахгүй. Тодорхойлолт: `index.css`. */}
        <div className="login-vignette absolute inset-0" />
      </div>

      {/*
        ⚠️ Карт нь ХАГАС тунгалаг (`bg-panel/85`). Доор нь ҮРГЭЛЖ бараан
        scrim байдаг тул композит нь хоёр горимд хоёуланд нь урьдчилан
        таамаглагдана: гэрэлд цайвар (ink 12.6:1), харанхуйд бараан
        (ink 12.9:1). Дэвсгэр зураг хар ч бай, цагаан ч бай ялгаагүй.
        `bg-panel/85`-ыг цаашид сулруулбал энэ баталгаа задарна.

        Талбар, товч нь тунгалаг БУС хэвээр — оролт дээр видео мэдрэгдэхгүй.
      */}
      <div
        className="relative w-full max-w-[380px] rounded-card border border-hair/60 bg-panel/85
          p-5 shadow-pop backdrop-blur-xl sm:p-6"
      >
        <div className="mb-6 flex flex-col items-center text-center">
          <img
            src="/logo.svg"
            alt="Цаатан"
            className="brand-mark h-16 w-auto"
            width={102}
            height={80}
          />
          <h1 className="mt-3 text-h1">Цаатан</h1>
          <p className="mt-1 text-meta text-ink-2">Гутлын нөөц, борлуулалт, баталгаа</p>
        </div>

        {challenge ? (
          <form onSubmit={submitTotp}>
            <Field label="Баталгаажуулах код">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                maxLength={6}
                autoFocus
                required
                className="num text-center text-lg tracking-[0.35em]"
              />
            </Field>
            <p className="mt-2 text-sm text-ink-2">
              Баталгаажуулах аппад харагдах 6 оронтой кодыг оруулна уу.
            </p>

            {error ? (
              <p role="alert" className="mt-3.5 text-sm text-alert-ink">{error}</p>
            ) : null}

            <Button type="submit" className="mt-5 w-full" disabled={busy || code.length !== 6}>
              {busy ? 'Шалгаж байна…' : 'Баталгаажуулах'}
            </Button>
            <button
              type="button"
              onClick={back}
              className="mt-3 w-full text-sm text-accent-700 hover:underline"
            >
              Буцах
            </button>
          </form>
        ) : (
          <form onSubmit={submit}>
            <div className="space-y-3.5">
              <Field label="Хэрэглэгчийн нэр">
                <Input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  autoFocus
                  required
                />
              </Field>
              <Field label="Нууц үг">
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </Field>
            </div>

            {error ? (
              <p role="alert" className="mt-3.5 text-sm text-alert-ink">{error}</p>
            ) : null}

            <Button type="submit" className="mt-5 w-full" disabled={busy}>
              {busy ? 'Шалгаж байна…' : 'Нэвтрэх'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
